import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Home from "../page/Home";
import { Dot, logo } from "../assets/images";
import Products from "../page/Products";
import Contact from "../page/Contact";
import { useScrollControl } from "../hook/ScrollContext";
import Pricing from "../page/Pricing";
import Download from "../page/Download";
import HomeIcon from "../assets/images/HomeIcon";
import ProductIcon from "../assets/images/ProductIcon";
import ContactIcon from "../assets/images/ContactIcon";
import CookieBanner from "../component/CookieBanner";
const DesktopLayout = () => {
    const [searchParams, setSearchParams] = useSearchParams("page=Home");
    const nav = useNavigate();
    const { scrollEnabled } = useScrollControl();
    const [openMenu, setOpenMenu] = useState<boolean>(false);
    const [listComponent, setListComponent] = useState([
        {
            id: 1,
            name: "Home",
            checked: true,
            component: Home,
            icon: HomeIcon,
        },
        {
            id: 2,
            name: "Features",
            checked: false,
            component: Products,
            icon: ProductIcon
        },
        {
            id: 3,
            name: "Pricing",
            checked: false,
            component: Pricing,
            icon: ContactIcon
        },
        {
            id: 3,
            name: "Download",
            checked: false,
            component: Download,
            icon: ContactIcon
        },
        {
            id: 4,
            name: "Contact",
            checked: false,
            component: Contact,
            icon: ContactIcon
        },
    ]);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });
    const handleChoose = (index: number) => {
        if (window.innerWidth < 1024) setOpenMenu(false)
        const updated = listComponent.map((item, i) => ({
            ...item,
            checked: i === index,
        }));
        searchParams.set("page", updated[index].name);
        setSearchParams(searchParams);
        setListComponent(updated);
        setActiveIndex(index);
    };


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenu(false);
            }
        };

        if (openMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openMenu, setOpenMenu]);

    useEffect(() => {
        const params = searchParams.get("page");
        const index = listComponent.findIndex((item) => item.name === params);
        setListComponent(
            listComponent.map((item, i) => ({
                ...item,
                checked: i === index,
            }))
        );
        setActiveIndex(index);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [searchParams]);

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            const tab = container.children[activeIndex] as HTMLDivElement;
            if (tab) {
                const { offsetLeft, offsetWidth } = tab;
                setIndicatorStyle({
                    width: offsetWidth,
                    left: offsetLeft,
                });
            }
        }
    }, [activeIndex]);

    useEffect(() => {
        const handleScroll = (e: WheelEvent) => {
            if (window.innerWidth < 1024) {
                e.preventDefault();
                return;
            }
            if (!scrollEnabled) {
                e.preventDefault();
                return;
            }
            e.preventDefault();
            if (e.deltaY > 0) {
                setActiveIndex((prev) => {
                    const newIndex = Math.min(prev + 1, listComponent.length - 1);
                    if (newIndex !== prev) handleChoose(newIndex);
                    return newIndex;
                });
            } else {
                setActiveIndex((prev) => {
                    const newIndex = Math.max(prev - 1, 0);
                    if (newIndex !== prev) handleChoose(newIndex);
                    return newIndex;
                });
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
        };

        window.addEventListener("wheel", handleScroll, { passive: false });

        return () => {
            window.removeEventListener("wheel", handleScroll);
        };
    }, [listComponent, scrollEnabled]);

    return (
        <div className={`w-screen h-screen overflow-hidden relative  no-scrollbar`}>
            <CookieBanner />
            <div ref={menuRef} className={`absolute top-0 w-[80%] h-full bg-[#FFFFFF26] backdrop-blur-[27.6px] z-[40] duration-300 ease-in-out rounded-tr-2xl rounded-br-2xl overflow-hidden ${openMenu ? "left-0" : "left-[-100%]"}`}>
                <div className="mt-[115px] space-y-6 px-4">
                    {listComponent.map((item, index) => (
                        <div
                            key={item.id}
                            onClick={() => handleChoose(index)}
                            className={`select-none relative py-2 px-4 text-[24px] font-inter text-[#FFFFFF] cursor-pointer transition-all duration-300 ${item.checked ? "font-bold bg-[#00000052] " : "font-normal"
                                }`}
                        >
                            {item.name}
                        </div>
                    ))}
                </div>
            </div>
            <div
                style={{
                    backdropFilter: 'blur(27.6px)',
                }}
                className={`absolute z-30 bottom-5 left-1/2 -translate-x-1/2 px-4 py-2 flex items-center gap-x-2 lg:text-[14px] text-[12px] font-inter text-[#FFFFFF] rounded-3xl bg-[#00000052]`}>
                <p className="font-normal">Copyright © 2025</p>
                <p className="font-semibold">VIYUME</p>
            </div>
            <div className="w-fit lg:flex hidden items-center justify-center px-4 py-2 gap-x-16 bg-[#00000052] rounded-2xl absolute z-30 bottom-5 right-8">
                <div className="cursor-pointer text-[16px] font-inter text-white font-normal" onClick={() => {
                    nav({
                        pathname: "/legal",
                        search: `?page=Copyright`,
                    });
                }}>Copyright</div>
                <div className="cursor-pointer text-[16px] font-inter text-white font-normal" onClick={() => {
                    nav({
                        pathname: "/legal",
                        search: `?page=Privacy`,
                    });
                }}>Privacy Policy</div>
                <div className="cursor-pointer text-[16px] font-inter text-white font-normal" onClick={() => {
                    nav({
                        pathname: "/legal",
                        search: `?page=Terms`,
                    });
                }}>Terms of Service</div>
            </div>
            <div className="lg:flex max-lg:hidden mt-[26px] container absolute z-20 w-full">
                <img src={logo} alt="Logo" className="w-[56px] max-lg:hidden" />
                <div className="w-full max-lg:hidden">
                    <div ref={containerRef} className="relative px-4 py-2 flex items-center justify-center gap-[120px] h-full">
                        {listComponent.map((item, index) => (
                            <div
                                key={item.id}
                                onClick={() => handleChoose(index)}
                                className={`relative select-none px-6 py-3 bg-[#00000052] rounded-full text-[20px] font-sf text-white cursor-pointer transition-all duration-300  backdrop-blur-[27.6px] ${item.checked ? "font-bold bg-[#000000={() => border-l-[1.5px] border-white" : "font-normal"
                                    }`}
                            >
                                {item.name}
                            </div>
                        ))}
                        <div
                            className="absolute top-1/2 -translate-y-1/2 pointer-events-none transition-all duration-500 ease-in-out"
                            style={{
                                left: `${indicatorStyle.left}px`,
                                width: `${indicatorStyle.width}px`,
                            }}
                        >
                            <div className="absolute -left-[42px] top-1/2 -translate-y-1/2 w-[30px] h-[6px] bg-[#94DE1D] rounded-2xl" />
                            <div className="absolute -right-[42px] top-1/2 -translate-y-1/2 w-[30px] h-[6px] bg-[#94DE1D] rounded-2xl" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Page content */}
            <div className="relative w-full h-full">
                <div className="w-full relative lg:hidden lg:mt-[55px] px-4 ">
                    {/* <div className="w-12 h-12 rounded-full border border-[#FFFFFF96] bg-[#FFFFFF26] flex items-center justify-center cursor-pointer" onClick={() => { setOpenMenu(!openMenu) }}>
                        <img src={Dot} alt="Logo" className="w-6 h-6" />
                    </div> */}
                    <div className="w-12 h-12 rounded-full border border-[#FFFFFF96] bg-[#FFFFFF26]  cursor-pointer absolute right-4 z-20 top-5" onClick={() => { setOpenMenu(!openMenu) }} ref={menuRef}>
                        <div className="relative w-full h-full flex items-center justify-center">
                            <img src={Dot} alt="Logo" className="w-6 h-6" />
                            {/* {openMenu &&
                                <div className="flex items-center justify-center w-[330px] gap-x-4 bg-[#00000052] backdrop-blur-2xl px-2 rounded-2xl border border-[#FFFFFF96] absolute z-20 top-[52px] right-0 h-[92px]">
                                    {listComponent.map((val, index) => {
                                        const Icon = val.icon;
                                        return (
                                            <div key={val.id} className="space-y-2" onClick={() => handleChoose(index)}>
                                                <div className="w-6 h-6 mx-auto">
                                                    <Icon active={true} />
                                                </div>
                                                <p className="text-[14px] text-white font-inter">{val.name}</p>
                                            </div>
                                        );
                                    })}

                                </div>
                            } */}
                        </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 top-8 z-20">
                        <img src={logo} alt="Logo" className="w-[56px]" />
                    </div>
                </div>
                <AnimatePresence>
                    {listComponent.map((item, index) =>
                        item.checked ? (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, ease: "easeInOut" }}
                                className="absolute w-full h-full z-10 md:mt-0 "
                            >
                                {item.component && <item.component key={item.id} />}
                            </motion.div>
                        ) : null
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default DesktopLayout;
